/**
 * Created by rhinov15 on 19/01/2016.
 *
 * Node displaying a room entity
 */

import { EntityNode } from "./EntityNode";
import { GameObjectManager } from "../GameObjectManager";
import { DimensionNode } from "../Nodes/DimensionNode";
import { ActionStateEnum } from "../../state/actionState/ActionState";
import { PolyNode } from "../Nodes/PolyNode";
import { LabelButtonNode } from "../Nodes/LabelButtonNode";
import { EntityManager } from "../EntityManager";
import { Utils } from "../../utils/Utils";
import { CocosConstants } from "../../../../constants";
import { PlanManager } from "../../../../cocosPlan";
import { Coating, Commands, ComponentConstants, Event, Events, eventsManager, Floor, Functionality, math, Room, roomManager, SmartDesignerSolution } from "@rhinov/savane-js";

declare var cc: any;

export class RoomNode extends EntityNode {

    static _DEFAULT_COLOR = new cc.color(230, 230, 230, 255);
    static _UNDER_COLOR = new cc.color(242, 242, 242, 255);
    static _HOVER_ORDERED_COLOR = new cc.color('c0d1ae');
    static _ORDERED_COLOR = new cc.color('d5e8c1');
    static _OUTSIDE_COLOR = new cc.color(196, 188, 173, 255);
    static _HOVER_OUTSIDE_COLOR = new cc.color(176, 168, 153, 255);
    static _SELECTED_OUTSIDE_COLOR = new cc.color(176, 168, 153, 255);
    static _HOVER_COLOR = new cc.color(210, 210, 210, 255);
    static _SELECTED_COLOR = new cc.color(210, 210, 210, 255);
    static _UNAMED_COLOR = new cc.color(255, 130, 120, 255);
    static _HOVER_UNAMED_COLOR = new cc.color(230, 120, 110, 255);

    static _GARDEN_COLOR = new cc.color(0, 96, 0, 255);
    static _DRIVEWAY_COLOR = new cc.color(0, 0, 96, 255);
    static _TERRACE_COLOR = new cc.color(96, 0, 0, 255);
    static _SELECTED_GARDEN_COLOR = new cc.color(0, 64, 0, 255);
    static _SELECTED_DRIVEWAY_COLOR = new cc.color(0, 0, 64, 255);
    static _SELECTED_TERRACE_COLOR = new cc.color(64, 0, 0, 255);
    room: Room;

    constructor(room: Room) {
        super(room);

        // Get if we have to apply an alpha to the room polygone (if no background image then we can have a solid color, if background available then display with alpha so we can see through)
        var alpha = 80;
        // Compute color mask for room polygon
        this.maskColor = new cc.color(RoomNode._DEFAULT_COLOR.r, RoomNode._DEFAULT_COLOR.g, RoomNode._DEFAULT_COLOR.b, alpha);

        // Store the room entity for further things
        this.room = room;

        // Is there a screenshot taken for this room
        this.screenshot = false;
        // Is it an exterior room
        this.isExterior = false;

        // Dimension nodes to display room dimensions
        this.dimensions = [];
        this.dimensionsDatas = [];

        // SketchMode
        this.sketch = CocosConstants.sketchMode;

        // Node to store name label (room type) and area label (surface size) nodes
        this.roomExtra = new cc.Sprite();
        this.roomExtra.setName('RoomExtra' + room.id);
        // Room name and area text labels
        this.roomNameLabel = null;
        this.roomAreaLabel = null;
        // Don't show detailed height
        this.showDetailedHeight = false;

        // Check if the current room can be edited (i.e. is on current edited floor)
        if ((this.room.floor as Floor).id !== PlanManager.getInstance().world.currentScene!.currentFloor!.id) {
            // Not editable then retrieve opacity from PlanManager (and app settings)
            this.editable = false;
            this.maskColor.a = PlanManager.getInstance().opacityFloors;
        } else {
            // Room can be edited
            this.editable = true;
        }

        // Listener for background image addition (post to the room node creation)
        this.planUploaded = eventsManager.instance.addListener(Events.BACK_PLAN_IMPORT, (event: Event) => {});

        // Listener for background image addition (post to the room node creation)
        this.detailedHeightListener = eventsManager.instance.addListener(Events.SHOW_DETAILED_HEIGHT, (event: Event) => {
            this.showDetailedHeight = event.userData;
            this.updateName();
        });

        // Create room polygon to display room area
        this.polyNode = new PolyNode([], this.maskColor, []);
        this.addChild(this.polyNode);

        // Retrieve center of the biggest poly in the room to display datas
        var center = GameObjectManager.convertToCocosCoord(this.room.aestheticCenter);
        // Create room name label for the room node
        var name = (room.getComponent(ComponentConstants.ComponentType.Functionality) as Functionality).functionalityName;
        this.roomNameLabel = new LabelButtonNode();
        this.roomNameLabel.setString(name === null || name == '' ? 'Renommer la pièce' : name);
        this.roomNameLabel.setVisible(PlanManager.getInstance().mustShowRoomName);
        this.roomNameLabel.setName('RoomName');
        this.roomNameLabel.setAnchorPoint(new cc.p(0.5, 0.5));
        this.roomNameLabel.setPosition(center);
        // Create room area (m²) node
        this.roomAreaLabel = new cc.LabelTTF(this.getRoomAreaLabel(), 'Roboto-Light', 11, new cc.size(210, 100), cc.TEXT_ALIGNMENT_CENTER, cc.TEXT_ALIGNMENT_CENTER);
        this.roomAreaLabel.setVisible(PlanManager.getInstance().mustShowRoomArea);
        this.roomAreaLabel.setName('RoomArea');
        this.roomAreaLabel.disableStroke();
        this.roomAreaLabel.setAnchorPoint(new cc.p(0.5, 0.8));
        this.roomAreaLabel.setFontFillColor(new cc.color(150, 150, 150, 255));
        this.roomAreaLabel.setPosition(center);
        this.roomAreaLabel.setScale(1.0 / PlanManager.getInstance().getZoomScale());


        //DEBUG NODE WITH ROOM ID
        /*if (this.room.floor.id === PlanManager.getInstance().world.currentScene!.currentFloor!.id) {
            this.roomIdLabel = new cc.LabelTTF('' + this.room.id, 'Roboto-Light', 16, new cc.size(0, 20), cc.TEXT_ALIGNMENT_CENTER, cc.TEXT_ALIGNMENT_CENTER);
            this.roomIdLabel.setName('RoomLabel');
            this.roomIdLabel.setAnchorPoint(new cc.p(0.5, 1));
            this.roomIdLabel.setPosition(center);
            this.roomIdLabel.setFontFillColor(new cc.color(255, 0, 0, 255));
            this.roomIdLabel.zIndex = 999999;
            this.addChild(this.roomIdLabel);
        }*/


        // show smart icon if room contains solution
        let smartDesignerSolution = this.room.getComponent(ComponentConstants.ComponentType.SmartDesignerSolution) as SmartDesignerSolution;
        if(smartDesignerSolution && smartDesignerSolution.maxSolution > 0) {
            this.showSmartIcon(true);
        }

        // Add name and area label to the room extra node
        this.roomExtra.addChild(this.roomAreaLabel);
        this.roomExtra.addChild(this.roomNameLabel);
        // And add the room extra node to the room node
        this.addChild(this.roomExtra);

        // Coating handling
        this.tempCoating = null;

        // Update room triangles for the polygon to display
        this.updateTriangles();
    }

    getRoomAreaLabel (): string {
        var area = Math.round(this.room.area / 1000000) + ' m² - HSP ' + this.room.height / 1000 + 'm';
        if (this.showDetailedHeight) {
            area += ' - Sol ' + this.room.floorHeight / 1000 + 'm';
        }

        return (area);
    }

    // Called when the room is selected
    changeSelectedState (stateValue: boolean, triangles: any, zoom: number) {
        // Store selected state (true/false)
        this._isSelected = stateValue;
        // CLear dimension labels and nodes (it might not be selected we might not have to display the dimensions)
        this.clearDimensions();
        // Choose the color to use for the color fill (depending on selected state, background image, ordered, exterior)
        this.chooseColor();

        // Update room triangles again
        this.updateTriangles();

        // Now, check if selected, hovered...
        if ((this._isSelected || this._isHovered) || this.sketch) {
            // Get room inner point list
            var innerPList = this.room.getInnerPointsList(true);
            // Parse them all to create dimension nodes
            for (var i = 0; i < innerPList.points.length; i++) {
                // Get current begin and wall
                var begin = GameObjectManager.convertToCocosCoord(innerPList.points[i]);
                var beginWalls = innerPList.walls[i];
                var end;
                var endWalls;

                // Get second wall (and loop if last one)
                if (i >= innerPList.points.length - 1) {
                    end = GameObjectManager.convertToCocosCoord(innerPList.points[0]);
                    endWalls = innerPList.walls[0];
                } else {
                    end = GameObjectManager.convertToCocosCoord(innerPList.points[i + 1]);
                    endWalls = innerPList.walls[i + 1];
                }

                // Avoid dimensions that are thickness of a wall
                if ((beginWalls[0].id === beginWalls[1].id && endWalls[0].id === endWalls[1].id && endWalls[0].id === beginWalls[0].id) ||
                    (beginWalls[0].id === endWalls[0].id && beginWalls[1].id === endWalls[1].id)) {
                    continue;
                }

                // Compute dimension size length an middle
                var length = new cc.p((end.x - begin.x), (end.y - begin.y));
                var middle = {
                    x: begin.x + length.x * 0.5,
                    y: begin.y + length.y * 0.5
                };

                // Create new dimension node and add it to room extra node
                this.dimensions[i] = new DimensionNode(undefined);
                this.dimensions[i].setName('RoomDimension' + i);
                this.roomExtra.addChild(this.dimensions[i], 7);

                // Calculate angle
                var lengthDirect = new cc.p((end.x - begin.x), (end.y - begin.y));
                var lengthDistance = Math.sqrt((lengthDirect.x * lengthDirect.x) + (lengthDirect.y * lengthDirect.y));
                var angle = Math.atan2(-lengthDirect.y / lengthDistance, lengthDirect.x / lengthDistance);
                var angleDegree = angle * 360.0 / (Math.PI * 2.0);
                var normal = new cc.p(-lengthDirect.y / lengthDistance, lengthDirect.x / lengthDistance);

                // Create new dimension data from above
                this.dimensionsDatas[i] = {
                    position: middle,
                    normal: normal
                };

                if (this.sketch) {
                    var decal = (beginWalls[1].thickness / 10) + Utils.DIMENSION_DIST / zoom;
                    this.dimensions[i].setPosition(new cc.p(middle.x + decal * normal.x, middle.y + decal * normal.y));
                } else {
                    var decal = Utils.DIMENSION_DIST / zoom;
                    this.dimensions[i].setPosition(new cc.p(middle.x - decal * normal.x, middle.y - decal * normal.y));
                }

                // Fill dimension node with size length
                this.dimensions[i].setDimensionValue(lengthDistance, zoom);
                // Set rotation and flip to display it nicely
                this.dimensions[i].setRotation(angleDegree);
                this.dimensions[i].setFlipped((angleDegree > 90 && angleDegree <= 270) || (angleDegree < -90 && angleDegree >= -270));
                this.dimensions[i].setTag([beginWalls, endWalls]);
            }
        }
    }

    /**
     *
     * Inherited method from EntityNode
     *
     **/
    update (dt: number) {
        super.update(dt);

        if (PlanManager.getInstance().getAction() === ActionStateEnum.SelectedOrderRoom) {
            this.maskColor.a = 255;
            this.polyNode.setColor(this.maskColor);
            return;
        }

        if (!this.room.floor) {
            console.log("");
        }

        // Update polygon display color
        if ((this.room.floor as Floor).id !== PlanManager.getInstance().world.currentScene!.currentFloor!.id) {
            this.maskColor.a = PlanManager.getInstance().opacityFloors;
            this.polyNode.setColor(this.maskColor);
        }
    }

    // Hover node
    setHovered (val: boolean) {
        if (this._isHovered !== val) {
            this._isHovered = val;

            if (PlanManager.getInstance().getAction() === ActionStateEnum.SelectedOrderRoom) {
                this.chooseColor();
                this.updateTriangles();
            }
        }
    }

    setCoatingColor () {
        if (PlanManager.getInstance().getAction() === ActionStateEnum.SelectedOrderRoom) {
            this.polyNode.setColor(this.maskColor);
            return;
        }

        // To store the possible coating color for the floor
        var coatingColor = null;
        // If the room has a temporary floor coating then change the display color
        if (this.tempCoating !== null) {
            coatingColor = new cc.color(this.tempCoating);
        } else {
            // Get coating component
            var coatings = this.room.getComponents(ComponentConstants.ComponentType.Coating) as Coating[];

            // Check them all to find the floor coating
            for (var i = 0; i < coatings.length; i++) {
                // Floor coating ?
                if (coatings[i].hangType === Coating.HangType.floor) {
                    // Yes get its color
                    if (coatings[i].colors[0] && coatings[i].colors[0].realColor !== undefined) {
                        coatingColor = new cc.color(coatings[i].colors[0].realColor);
                    } else {
                        coatingColor = new cc.color(255, 0, 255);
                    }
                }
            }
        }

        if (coatingColor !== null) {
            coatingColor.a = this.maskColor.a;
            // Set polygon color
            this.polyNode.setColor(coatingColor);
        } else {
            this.polyNode.setColor(this.maskColor);
        }
    }

    // Update room triangles to display the room polygon
    updateTriangles () {
        try {
            // Retrieve points list and set it counter clock wise
            var points = this.room.getInnerPointsList(false);
            var roomsHolesList = this.room.getInnerRoomList(true);
            var holes = [];

            // Parse room hole list and add them to the holes list
            for (var i = 0; i < roomsHolesList.length; i++) {
                holes[i] = roomsHolesList[i].getInnerPointsList(false);
            }
            // Create a polygon node with the points and the holes
            this.polyNode.setVertices(points, holes);

            // Set color for coating
            this.setCoatingColor();

            // Update room name and area
            this.updateName();
        } catch (e) { }
    }

    // Update room name and area
    updateName () {
        // Get room component name and id
        var name = (this.room.getComponent(ComponentConstants.ComponentType.Functionality) as Functionality).functionalityName;
        var id = (this.room.getComponent(ComponentConstants.ComponentType.Functionality) as Functionality).functionalityId;
        // Position where to display the room area and name nicely
        var center = GameObjectManager.convertToCocosCoord(this.room.aestheticCenter);

        // If functionnality id is related to exterior then store this fact
        if (id === ComponentConstants.Functionalities.Veranda || id === ComponentConstants.Functionalities.Terrace ||
            id === ComponentConstants.Functionalities.Balcony || id === ComponentConstants.Functionalities.Patio ||
            id === ComponentConstants.Functionalities.Garden || id === ComponentConstants.Functionalities.Driveway ||
            id === ComponentConstants.Functionalities.Pool) {
            this.isExterior = true;
        } else {
            this.isExterior = false;
        }

        // Set the room area surface value and position
        this.roomAreaLabel.setAnchorPoint(new cc.p(0.5, 0.8));
        this.roomAreaLabel.setString(this.getRoomAreaLabel());
        this.roomAreaLabel.setPosition(center);

        // Set the room name and position
        this.roomNameLabel.setString(name === null || name == '' ? 'Renommer la pièce' : name);
        this.roomNameLabel.toggleIconMode(false);
        this.roomNameLabel.setPosition(center);
        this.roomNameLabel.redraw();

        // Check if the room name fits in the room size and if not toggle to icon mode
        var point = this.roomNameLabel.getMiddlePoints();
        var a = math.vec3.create();
        var b = math.vec3.create();
        // 150 = magic number that works sorry to prevent the room to be "just in the necessary space)
        a[0] = (point.a.x * 10) - 150;
        a[1] = point.a.y * 10;
        b[0] = (point.b.x * 10) + 150;
        b[1] = point.b.y * 10;
        // Whek if point A and B are in the room or not
        var testA = roomManager.getRoomAtPosition(a, PlanManager.getInstance().world.currentScene!.currentFloor!);
        var testB = roomManager.getRoomAtPosition(b, PlanManager.getInstance().world.currentScene!.currentFloor!);
        // If they aren't this means the label doesn't fit in the room area then toggle to icon mode and redraw
        if (!(testA && testB && testA.id === this.room.id && testB.id === this.room.id)) {
            this.roomNameLabel.toggleIconMode(true);
            this.roomNameLabel.redraw();
        }
    }

    // Clear dimension nodes and data from the room node
    clearDimensions () {
        // Loop variable
        var i;

        // Remove all dimension node from current node
        for (i = 0; i < this.dimensions.length; i++) {
            if (this.dimensions[i] !== undefined) {
                this.dimensions[i].removeFromParent(true);
            }
        }

        // Parse also all non room walls and hide their dimensions too
        for (i = 0; i < this.room.nonRoomedWalls.length; i++) {
            var wallNode = EntityManager.getInstance().getNode(this.room.nonRoomedWalls[i]);

            if (wallNode !== null) {
                wallNode.setForcedDimensions(false);
                wallNode.calculateNeighboorWallValues(this.room.nonRoomedWalls[i]);
            }
        }

        // Clear dimension node array
        this.dimensions = [];
    }

    // This sets a temporary coating when hovering with a coating
    setTempCoating (color: Coating) {
        this.tempCoating = color;
        this.updateTriangles();
    }

    // This selects the correct color for the polygon to display
    chooseColor () {
        // Get room functionality name
        var name = (this.room.getComponent(ComponentConstants.ComponentType.Functionality) as Functionality).functionalityName;

        if (PlanManager.getInstance().getAction() === ActionStateEnum.SelectedOrderRoom) {
            if (name === null || name === '' || this.room.isExterior) {
                if (this._isHovered) {
                    this.setColor(RoomNode._HOVER_UNAMED_COLOR);
                }
                else {
                    this.setColor(RoomNode._UNAMED_COLOR);
                }
            }
            else {
                if (this._isHovered) {
                    if (this.room.isOrdered) {
                        this.setColor(RoomNode._HOVER_ORDERED_COLOR);
                    }
                    else {
                        this.setColor(RoomNode._SELECTED_COLOR);
                    }
                }
                else {
                    if (this.room.isOrdered) {
                        this.setColor(RoomNode._ORDERED_COLOR);
                    }
                    else {
                        this.setColor(RoomNode._DEFAULT_COLOR);
                    }
                }
            }
            return;
        }

        // If selected or hovered
        if (this._isSelected || this._isHovered) {
            switch ((this.room.getComponent(ComponentConstants.ComponentType.Functionality) as Functionality).functionalityId) {
                case ComponentConstants.Functionalities.Terrace:
                    this.setColor(RoomNode._SELECTED_TERRACE_COLOR);
                    break;

                case ComponentConstants.Functionalities.Garden:
                    this.setColor(RoomNode._SELECTED_GARDEN_COLOR);
                    break;

                case ComponentConstants.Functionalities.Driveway:
                    this.setColor(RoomNode._SELECTED_DRIVEWAY_COLOR);
                    break;

                default:
                    this.setColor(this.isExterior ? RoomNode._SELECTED_OUTSIDE_COLOR : RoomNode._SELECTED_COLOR);
            }
        } else {
            switch ((this.room.getComponent(ComponentConstants.ComponentType.Functionality) as Functionality).functionalityId) {
                case ComponentConstants.Functionalities.Terrace:
                    this.setColor(RoomNode._TERRACE_COLOR);
                    break;

                case ComponentConstants.Functionalities.Garden:
                    this.setColor(RoomNode._GARDEN_COLOR);
                    break;

                case ComponentConstants.Functionalities.Driveway:
                    this.setColor(RoomNode._DRIVEWAY_COLOR);
                    break;

                default:
                    this.setColor(this.isExterior ? RoomNode._OUTSIDE_COLOR : RoomNode._DEFAULT_COLOR);
            }
        }

        if (this.room.isOrdered && !this.screenshot) {
            this.setColor(RoomNode._ORDERED_COLOR);
        }
    }

    startVisualiseMode (triangles: any, zoom: number) {}

    endVisualiseMode (triangles: any, zoom: number) {}

    // Store color for the room polygon to draw
    setColor (color: {r: number, g: number, b: number}) {
        this.maskColor.r = color.r;
        this.maskColor.g = color.g;
        this.maskColor.b = color.b;
    }

    // Deletes the listeners created by onEnter method
    removeListeners () {
        eventsManager.instance.removeListener(this.detailedHeightListener);
        eventsManager.instance.removeListener(this.planUploaded);
        eventsManager.instance.removeListener(this.mustAffArea);
        eventsManager.instance.removeListener(this.scrollListener);
        eventsManager.instance.removeListener(this.hideBackgroundListener);
        eventsManager.instance.removeListener(this.mustAffRoomName);
        eventsManager.instance.removeListener(this.namelisterner);
        //        eventsManager.instance.removeListener(this.planListener);
        eventsManager.instance.removeListener(this.changeOrderedState);
        eventsManager.instance.removeListener(this.sketchModeListener);
        eventsManager.instance.removeListener(this.commandListener);
    }

    // Object creation, create listeners
    onEnter () {
        super.onEnter();

        // Sketch mode
        this.sketchModeListener = eventsManager.instance.addListener(Events.TOGGLE_SKETCH_MODE, (event: Event) => {
            this.sketch = !this.sketch;
            this.changeSelectedState(false, null, PlanManager.getInstance().getZoomScale());
        });


        this.changeOrderedState = eventsManager.instance.addListener(Events.CHANGE_ROOM_ORDERED_STATE, (event: Event) => {
            var params = event.getUserData();
            if (params.room.id === this.room.id) {
                this.room.isOrdered = params.is_ordered;
                this.chooseColor();
                this.updateTriangles();
            }
        });

        // Plan changed
        /*        this.planListener = eventsManager.instance.addListener(Events.PLAN_STATE_CHANGED, function (event)
                {
                    this.updateTriangles();
                }.bind(this));*/

        // A command was executed
        this.commandListener = eventsManager.instance.addListener(Events.COMMAND_EXECUTED, (event: Event) => {
            // Get the executed command
            var command = event.getUserData();

            // If the command changes something for the room, recompute it to update display
            if ((command.name === Commands.CommandEnum.ScaleFloorCommand) || (command.name === Commands.CommandEnum.TranslateFloorCommand) ||
                (command.name === Commands.CommandEnum.SymetryFloorCommand) || (command.name === Commands.CommandEnum.RotateFloorCommand)) {
                // Recompute room surface because it might have changed (otherwise the label position will be wrong)
                this.room.triangulate();
                // Update triangles, label position etc etc
                this.changeSelectedState(this._isSelected, null, PlanManager.getInstance().getZoomScale());
            }
        });

        // Listener for a zoom level change
        this.scrollListener = eventsManager.instance.addListener(Events.ZOOM_SCROLLWIN_CHANGED, (event: Event) => {
            if (this.roomNameLabel !== null) {
                this.updateName();
            }
            if (this.roomAreaLabel !== null) {
                this.roomAreaLabel.setScale(1.0 / event.getUserData());
            }
            if (this.sketch) {
                this.changeSelectedState(false, null, PlanManager.getInstance().getZoomScale());
            } else {
                // Recompute room dimensions positions
                for (var i = 0; i < this.dimensions.length; i++) {
                    if (this.dimensions[i] !== undefined) {
                        this.dimensions[i].setPosition(
                            new cc.p(this.dimensionsDatas[i].position.x - this.dimensionsDatas[i].normal.x * Utils.DIMENSION_DIST / event.getUserData(),
                                this.dimensionsDatas[i].position.y - this.dimensionsDatas[i].normal.y * Utils.DIMENSION_DIST / event.getUserData()));
                    }
                }
            }
        });

        // Update alpha value in case the background has changed
        this.hideBackgroundListener = eventsManager.instance.addListener(Events.HIDE_BACKGROUND_PRESSED, (event: Event) => {
            if (this.editable) {
                this.maskColor.a = 255;
                if (event.getUserData()) {
                    this.maskColor.a = 80;
                }
                // Set polygon color
                this.setCoatingColor();
            }
        });

        // Hide/Display area surface (button in the floating bar)
        this.mustAffArea = eventsManager.instance.addListener(Events.TOGGLE_ROOM_AREA, (event: Event) => {
            this.roomAreaLabel.setVisible(event.getUserData());
        });

        // Hide/Display room name
        this.mustAffRoomName = eventsManager.instance.addListener(Events.TOGGLE_ROOM_NAME, (event: Event) => {
            this.roomNameLabel.setVisible(event.getUserData());
        });

        // Room functionality has changed, update color and polygon
        this.namelistener = eventsManager.instance.addListener(Events.UPDATE_MAINFUNCTIONALITY_CHANGE_NODE, (event: Event) => {
            this.chooseColor();
            this.clearDimensions();
            this.updateTriangles();
        });

        this.chooseColor();
        this.clearDimensions();
        this.updateTriangles();
    }

    // Object destructor
    onExit () {
        super.onExit();
        this.removeListeners();
    }

    // show icon indicating that the room as smart designer's solutions
    showSmartIcon (bool: boolean) {

        // check if current room has smart solutions, if it does, show the icon
        let smartDesignerComponent = this.room.getComponent(ComponentConstants.ComponentType.SmartDesignerSolution) as SmartDesignerSolution;
        if (smartDesignerComponent !== null && smartDesignerComponent.maxSolution > 0) {
            this.roomNameLabel.toggleSmartIcon(bool);
        } else {
            this.roomNameLabel.toggleSmartIcon(false);
        }

    }
}